import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useBoundStore } from '@Store/useBoundStore';
import { HttpService } from '@Services/index';
import { RoutesMap } from '@Router/router.types';
import { AuthErrorMap } from '@Services/errors/auth.errors';
import type { ServerErrorResponse } from '@Types/index';

const useVerifySecurityQuestion = (onError: (error: ServerErrorResponse) => void) => {
  const { customerId, stateToken, requestMfaType } = useBoundStore(
    (state) => state.resetPasswordSlice
  );

  return useMutation({
    mutationFn: async (answer: string) => {
      const response = await HttpService.post(
        `v1/onboarding/user/openauth/${customerId}/recovery/question`,
        {
          answer,
          customData: {
            stateToken: stateToken,
            needToConvert: true,
            mfaDestination: requestMfaType,
          },
        }
      );
      return response?.data?.token || '';
    },
    onError: (error: ServerErrorResponse) => {
      onError(error);
    },
  });
};
export const useSecurityQuestionLogic = () => {
  const [securityQuestion, setSecurityQuestion] = useState<string>('');
  const [error, setError] = useState<string | undefined>(undefined);
  const { navigateToNextPage } = useNavigationManager();
  const { isUs, setToken } = useBoundStore((state) => state.resetPasswordSlice);
  const { t } = useTranslation();
  const { mutateAsync } = useVerifySecurityQuestion((errData) => {
    if (errData?.response?.data?.errors[0]?.code === AuthErrorMap.MFA_CODE_IS_INVALID.errorCode) {
      setError(
        isUs ? t('recovery.securityQuestion.ssnError') : t('recovery.securityQuestion.idError')
      );
    } else {
      setError(errData?.response?.data?.errors[0]?.localizedMessage || '');
    }
  });

  const handleVerifySecurityQuestion = () => {
    mutateAsync(securityQuestion).then((token) => {
      setToken(token);
      navigateToNextPage(RoutesMap.SECURITY_QUESTION);
    });
  };

  useEffect(() => {
    if (securityQuestion.length === 4) {
      handleVerifySecurityQuestion();
    }
  }, [securityQuestion]);

  const clearError = () => {
    setError(undefined);
  };

  return {
    securityQuestion,
    setSecurityQuestion,
    error,
    clearError,
    handleVerifySecurityQuestion,
  };
};
